/*
  Paint A Moment add-ons (custom code, no app). Oct 2026.
  - Product page: Painter's Upgrade Pack card (snippets/pam-addon-pdp.liquid)
  - Cart: setup track + offers (snippets/pam-cart-upsells.liquid), inline frame picker (snippets/pam-frame-picker.liquid)
  - Fixes the theme's cart "Change variant" dropdown, which dropped line-item properties (the customer's photo).
  Every add-on line carries a hidden _addon_source property (pdp / cart) for reporting.
*/
(() => {
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const money = (cents) => {
    try { return Shopify.formatMoney(cents) } catch (e) { return '£' + (cents / 100).toFixed(2) }
  }

  const buzz = () => { try { navigator.vibrate && navigator.vibrate(12) } catch (e) {} }

  // Small burst of brand-colour dots from an element. Purely decorative.
  const burst = (el) => {
    if (!el || reduceMotion) return
    const r = el.getBoundingClientRect()
    const host = document.createElement('div')
    host.className = 'pam-burst'
    host.style.left = (r.left + r.width / 2) + 'px'
    host.style.top = (r.top + r.height / 2) + 'px'
    const colours = ['#2EB3BA', '#1D8A90', '#E8735A', '#F2C14E', '#2EB3BA']
    for (let i = 0; i < 14; i++) {
      const d = document.createElement('i')
      const a = (Math.PI * 2 * i) / 14 + Math.random() * 0.4
      const dist = 26 + Math.random() * 28
      d.style.setProperty('--x', Math.cos(a) * dist + 'px')
      d.style.setProperty('--y', Math.sin(a) * dist + 'px')
      d.style.background = colours[i % colours.length]
      host.appendChild(d)
    }
    document.body.appendChild(host)
    setTimeout(() => host.remove(), 800)
  }

  const addItems = (items) => fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items })
  })

  const refresh = (response) => {
    if (typeof window.refreshCartContents === 'function') return window.refreshCartContents(response)
    location.reload()
  }
  // A swap is a change, not an add: show the theme's "cart updated" note rather than "item added".
  const refreshAsUpdate = (response) => refresh(response && response.ok ? { ok: true, url: '/cart/change.js' } : response)

  /* ---------------- Product page ---------------- */
  const initPdp = () => {
    const card = document.querySelector('[data-pam-pdp]')
    if (!card || card.dataset.ready) return
    card.dataset.ready = '1'
    const check = card.querySelector('[data-pam-pdp-check]')
    const packId = Number(card.dataset.variant)
    const packPrice = Number(card.dataset.price)
    const form = card.closest('form')

    // Keep the Add to cart price honest: base variant price, plus the pack when ticked.
    let writing = false
    const priceEls = () => (form ? form.querySelectorAll('.btn-atc-price') : [])
    const setPrices = () => {
      priceEls().forEach((el) => {
        if (!el.dataset.pamBase) return
        writing = true
        el.textContent = money(Number(el.dataset.pamBase) + (check.checked ? packPrice : 0))
        writing = false
      })
    }
    const readBase = (el) => {
      const cents = Math.round(parseFloat(el.textContent.replace(/[^0-9.]/g, '')) * 100)
      if (!isNaN(cents)) el.dataset.pamBase = cents
    }
    priceEls().forEach(readBase)
    if (form) {
      new MutationObserver(() => {
        if (writing) return
        priceEls().forEach((el) => {
          const shown = Math.round(parseFloat(el.textContent.replace(/[^0-9.]/g, '')) * 100)
          const ours = Number(el.dataset.pamBase || 0) + (check.checked ? packPrice : 0)
          if (shown !== ours) { readBase(el); setPrices() }
        })
      }).observe(form, { subtree: true, childList: true, characterData: true })
    }

    check.addEventListener('change', () => {
      card.classList.toggle('is-on', check.checked)
      setPrices()
      if (check.checked) { buzz(); burst(card.querySelector('.pam-up__box')) }
    })

    // Add the pack to the cart just before the kit. The theme's handler then adds the kit
    // and refreshes the cart drawer, so both show together.
    const wrap = () => {
      const original = window.onSubmitAtcForm
      if (typeof original !== 'function' || original.pamWrapped) return false
      const wrapped = async (f, event) => {
        if (f === form && check.checked) {
          event && event.preventDefault()
          try {
            await addItems([{ id: packId, quantity: 1, properties: { _addon_source: 'pdp' } }])
          } catch (e) {}
        }
        return original(f, event)
      }
      wrapped.pamWrapped = true
      window.onSubmitAtcForm = wrapped
      return true
    }
    if (!wrap()) window.addEventListener('load', wrap)
  }

  /* ---------------- Cart ---------------- */

  // Swap a cart line to another variant, keeping its properties (the uploaded photo) and position-independent data.
  window.pamSwapLine = async (key, variantId, quantity) => {
    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json()
    const line = cart.items.find((i) => i.key === key)
    const item = { id: Number(variantId), quantity: Number(quantity || (line && line.quantity) || 1) }
    if (line && line.properties && Object.keys(line.properties).length) item.properties = line.properties
    if (line && line.selling_plan_allocation) item.selling_plan = line.selling_plan_allocation.selling_plan.id
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
    return addItems([item])
  }

  // The theme's "Change variant" dropdown re-added the item without its properties, losing the
  // customer's photo. Route it through pamSwapLine instead.
  const fixVariantSwitcher = () => {
    window.onChangeCartLineItemVariant = async (select) => {
      select.disabled = true
      const response = await window.pamSwapLine(select.dataset.lineItemKey, select.value, select.dataset.quantity)
      refreshAsUpdate(response)
    }
  }

  const celebrateKey = 'pam-cart-done'
  const giftKey = 'pam-gift-added'
  let gifting = false
  const afterCartRender = () => {
    const box = document.querySelector('#offcanvas-cart [data-pam-cart], #cart [data-pam-cart]')
    if (box) {
      const done = Number(box.dataset.done)
      let before = 0
      try { before = Number(sessionStorage.getItem(celebrateKey) || 0) } catch (e) {}
      if (before && done > before) {
        box.classList.add('is-levelup')
        const node = box.querySelectorAll('.pam-cu__track li')[done - 1]
        setTimeout(() => burst(node && node.querySelector('i')), 150)
        setTimeout(() => box.classList.remove('is-levelup'), 1200)
      }
      try { sessionStorage.setItem(celebrateKey, String(done)) } catch (e) {}
    }

    // Christmas gift: add the free pack once per session when the kits reach £50, and take a gifted
    // pack back out if the kits drop under £50 again, so it is never charged.
    const gift = document.querySelector('#offcanvas-cart [data-pam-gift], #cart [data-pam-gift]')
    if (gift && gift.dataset.pamGiftRemove && !gifting) {
      gifting = true
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: gift.dataset.pamGiftRemove, quantity: 0 })
      }).then(refreshAsUpdate).finally(() => { gifting = false })
    } else if (gift && gift.dataset.pamAutogift && !gifting) {
      let given = false
      try { given = sessionStorage.getItem(giftKey) === '1' } catch (e) {}
      if (!given) {
        gifting = true
        try { sessionStorage.setItem(giftKey, '1') } catch (e) {}
        addItems([{ id: Number(gift.dataset.pamAutogift), quantity: 1, properties: { _addon_source: 'gift' } }])
          .then(refreshAsUpdate).finally(() => { gifting = false })
      }
    }

    // Accessories travel inside a kit box: block checkout for an accessories-only cart.
    const accOnly = !!document.querySelector('[data-pam-acc-only]')
    document.querySelectorAll('#offcanvas-cart [name="checkout"], #cart [name="checkout"]').forEach((b) => {
      if (accOnly) { b.disabled = true; b.dataset.pamBlocked = '1' } else if (b.dataset.pamBlocked) { b.disabled = false; delete b.dataset.pamBlocked }
    })
  }

  // Belt and braces for the accessories-only rule: catch the checkout click itself, whatever the
  // button's disabled state (the drawer can be re-rendered by the theme after we set it).
  const blockAccOnlyCheckout = (e) => {
    const btn = e.target.closest && e.target.closest('[name="checkout"], [href^="/checkout"]')
    if (!btn) return
    const scope = btn.closest('#offcanvas-cart, #cart') || document
    const notice = scope.querySelector('[data-pam-acc-only]')
    if (!notice) return
    e.preventDefault()
    e.stopPropagation()
    notice.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' })
    notice.classList.remove('is-pulse'); void notice.offsetWidth; notice.classList.add('is-pulse')
  }
  document.addEventListener('click', blockAccOnlyCheckout, true)

  document.addEventListener('click', async (e) => {
    const add = e.target.closest('[data-pam-add]')
    if (add) {
      e.preventDefault()
      if (add.disabled) return
      add.disabled = true
      add.classList.add('is-busy')
      buzz()
      burst(add)
      const response = await addItems([{ id: Number(add.dataset.pamAdd), quantity: 1, properties: { _addon_source: add.dataset.pamSource || 'cart' } }])
      refresh(response)
      return
    }

    const swap = e.target.closest('[data-pam-swap]')
    if (swap) {
      e.preventDefault()
      const fp = swap.closest('[data-pam-fp]')
      if (!fp || fp.classList.contains('is-busy')) return
      fp.classList.add('is-busy')
      fp.querySelectorAll('.pam-fp__opt').forEach((o) => { o.classList.remove('is-on'); o.setAttribute('aria-checked', 'false') })
      swap.classList.add('is-on')
      swap.setAttribute('aria-checked', 'true')
      buzz()
      const response = await window.pamSwapLine(fp.dataset.key, swap.dataset.pamSwap, fp.dataset.qty)
      refreshAsUpdate(response)
      return
    }

    const goto = e.target.closest('[data-pam-goto-frame]')
    if (goto) {
      const scope = goto.closest('#offcanvas-cart, #cart') || document
      const fp = scope.querySelector('[data-pam-fp]')
      if (fp) {
        fp.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' })
        fp.classList.remove('is-pulse'); void fp.offsetWidth; fp.classList.add('is-pulse')
      }
    }
  })

  const init = () => {
    initPdp()
    fixVariantSwitcher()
    afterCartRender()
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init)
  else init()
  // cart.js defines its own onChangeCartLineItemVariant; make sure ours wins after all deferred scripts run.
  window.addEventListener('load', fixVariantSwitcher)
  window.addEventListener('updated.ecomify.cart', afterCartRender)
  let guardTimer
  const watchCart = () => {
    const el = document.querySelector('#offcanvas-cart')
    if (!el) return
    new MutationObserver(() => { clearTimeout(guardTimer); guardTimer = setTimeout(afterCartRender, 60) }).observe(el, { childList: true, subtree: true })
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCart)
  else watchCart()
  document.addEventListener('shopify:section:load', initPdp)
})()
